// utils/contributorCapture.js — Anamoria SPA
// v1.0 — NEW-163 Increment B (2026-10-30): single source of truth for the contributor capture
//   entry points, extracted from ContributorBottomNav v1.3 so the bottom nav and the group-note
//   Respond row (GroupNoteBanner v1.2) can never drift apart. SPA build spec v0.2 §1.
//
//   - captureTargets({ spaceId, note }) → { record, write, photo, video } route strings on the
//     one contributor rail (/contributing, Track B B-2), each with `?note=<id>` appended when a
//     group-note id is supplied (the Respond row). Without `note` the strings are byte-identical
//     to what ContributorBottomNav v1.3 navigated to.
//   - openPhotoPicker(input) / photoFileFromChange(e): the two halves of the Safari-safe photo
//     picker. The INPUT ELEMENT stays in each call site (a ref), because WebKit requires the
//     .click() to happen in the same task as the user gesture — sharing the ref through a hook
//     was ruled out at build (spec C-a). Only the logic is shared.
//   - findGroupNote(groupNotes, id): resolves a note id against the space GET's groupNotes
//     ({ current, earlier[] }) → { note, isCurrent } or null. Used by the capture pages to quote
//     the note (copy v1.0 B-8) and by the response card for its pill (B-6a/b/c).
//   - readNoteParam(searchParams): the `?note=` value, trimmed, or null.

export const CONTRIBUTOR_NAV_BASE = '/contributing';
export const NOTE_PARAM = 'note';

/** Route strings for the four capture pages. `note` (a group_notes id) appends `?note=<id>`. */
export function captureTargets({ spaceId, note = null, navBase = CONTRIBUTOR_NAV_BASE } = {}) {
  const q = note ? `?${NOTE_PARAM}=${encodeURIComponent(note)}` : '';
  const base = `${navBase}/${spaceId}`;
  return {
    record: `${base}/record${q}`,
    write: `${base}/write${q}`,
    photo: `${base}/photo${q}`,
    video: `${base}/video${q}`,
  };
}

/** Open the hidden file input. Must be called synchronously inside the click handler (Safari). */
export function openPhotoPicker(input) {
  if (!input) return;
  input.value = ''; // reset so re-selecting the same file fires change again
  input.click();
}

/** The chosen File from the input's change event, or null when the picker was cancelled. */
export function photoFileFromChange(e) {
  return e?.target?.files?.[0] || null;
}

/** `?note=` from a URLSearchParams (react-router useSearchParams), or null. */
export function readNoteParam(searchParams) {
  const v = searchParams?.get?.(NOTE_PARAM);
  const t = typeof v === 'string' ? v.trim() : '';
  return t || null;
}

/** Resolve a note id against groupNotes { current, earlier[] }. */
export function findGroupNote(groupNotes, id) {
  if (!id || !groupNotes) return null;
  if (groupNotes.current && groupNotes.current.id === id) return { note: groupNotes.current, isCurrent: true };
  const hit = (groupNotes.earlier || []).find((n) => n && n.id === id);
  return hit ? { note: hit, isCurrent: false } : null;
}
